/**
 * SloDashboard.tsx — SLO Tracker dashboard (/dashboard/slo)
 *
 * Purpose:    SLO/SLI editor, error budget time-series chart, and burn-rate alert badges.
 * Inputs:     useSloData hook (port 3835)
 * Outputs:    SloEditor + ErrorBudgetChart + BurnRateBadge
 * Constraints: 7-state AsyncScreen; chart rendered with inline SVG path (no recharts dep)
 * SOT:        T-P3-E5-W2-S4-T01
 */

import { useState } from 'react'
import { AsyncScreen } from '../../components/AsyncScreen'
import {
  useSloData,
  type BurnRateAlert,
} from '../../hooks/useSloData'
import { ErrorBudgetChart } from './ErrorBudgetChart'
import { SloEditor } from './SloEditor'

// ---------------------------------------------------------------------------
// Burn-rate badge
// ---------------------------------------------------------------------------

function BurnRateBadge({ alert }: { alert: BurnRateAlert }) {
  const color =
    alert.severity === 'critical'
      ? 'border-red-200 dark:border-red-700/50 bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300'
      : 'border-yellow-200 dark:border-yellow-700/50 bg-yellow-50 dark:bg-yellow-950/40 text-yellow-700 dark:text-yellow-300'
  return (
    <div className={`rounded-lg border px-4 py-3 ${color}`}>
      <p className="text-xs font-semibold uppercase">{alert.severity}</p>
      <p className="text-sm">{alert.sloName}</p>
      <p className="mt-1 text-xs">
        Burn rate: <strong>{alert.currentBurnRate.toFixed(2)}x</strong> (threshold {alert.threshold.toFixed(2)}x)
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function SloSkeleton() {
  return (
    <div className="animate-pulse space-y-3 p-6" aria-hidden>
      <div className="h-20 rounded-md bg-brand-surface-2" />
      <div className="h-10 rounded-md bg-brand-surface-2" />
      <div className="h-10 rounded-md bg-brand-surface-2" />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function SloDashboard() {
  const { state, data, error, refresh, saveSlo, deleteSlo } = useSloData()
  const [editingId, setEditingId] = useState<string | 'new' | null>(null)

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-brand-ink">SLO Tracker</h1>
          <p className="mt-1 text-sm text-brand-muted">
            Service Level Objectives, error budgets, and burn-rate alerts
          </p>
        </div>
        <button type="button" onClick={() => { void refresh() }} className="rounded-md border border-brand-card px-3 py-1.5 text-sm text-brand-secondary hover:bg-brand-surface-2">
          Refresh
        </button>
      </div>

      <AsyncScreen
        state={state}
        error={error}
        onRetry={() => { void refresh() }}
        loadingSkeleton={<SloSkeleton />}
        emptyMessage="No SLOs defined yet"
        emptyAction={
          <button
            type="button"
            onClick={() => setEditingId('new')}
            className="rounded-md bg-sky-700 px-4 py-1.5 text-sm font-medium text-white hover:bg-sky-500"
          >
            Create your first SLO
          </button>
        }
      >
        {data && (
          <div className="space-y-8">
            {data.burnRateAlerts.length > 0 && (
              <section>
                <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-brand-secondary">
                  Burn-rate alerts
                </h2>
                <div className="grid gap-3 sm:grid-cols-2">
                  {data.burnRateAlerts.map((alert, i) => (
                    <BurnRateBadge key={i} alert={alert} />
                  ))}
                </div>
              </section>
            )}

            <section>
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-brand-secondary">
                  SLO definitions
                </h2>
                <button
                  type="button"
                  onClick={() => setEditingId('new')}
                  className="text-xs text-sky-700 dark:text-sky-400 hover:underline"
                >
                  + New SLO
                </button>
              </div>

              {editingId === 'new' && (
                <div className="mb-4">
                  <SloEditor onSave={saveSlo} onCancel={() => setEditingId(null)} />
                </div>
              )}

              <div className="space-y-4">
                {data.slos.map((slo) => {
                  const points = data.errorBudgets[slo.id] ?? []
                  return (
                    <div key={slo.id} className="rounded-lg border border-brand-subtle bg-brand-surface/50 p-4">
                      {editingId === slo.id ? (
                        <SloEditor
                          initial={slo}
                          onSave={saveSlo}
                          onCancel={() => setEditingId(null)}
                        />
                      ) : (
                        <>
                          <div className="mb-3 flex items-center justify-between">
                            <div>
                              <h3 className="font-medium text-brand-ink">{slo.name}</h3>
                              <p className="text-xs text-brand-muted">
                                Target: {slo.targetPercent}% · Window: {slo.window}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => setEditingId(slo.id)}
                                className="text-xs text-sky-700 dark:text-sky-400 hover:underline"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => { void deleteSlo(slo.id) }}
                                className="text-xs text-red-700 dark:text-red-400 hover:underline"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                          <ErrorBudgetChart points={points} />
                        </>
                      )}
                    </div>
                  )
                })}
              </div>
            </section>
          </div>
        )}
      </AsyncScreen>
    </div>
  )
}
